import React from 'react';
import './DetailsPage.scss';
import { Icon, Card, Typography, Grid, LabelValue, Loader, Button } from '@design-system/index';
import { OrdersListLine, Pdf } from '@assets/images/icons';
import { useDetailsPageVM } from './useDetailsPageVM';
import { formatDate } from '@utils/dates';
import { CONTRIBUTION_TYPE, ORDER_STATUS, PAYMENT_METHOD } from '@utils/enumTranslation';
import { OrderProduct } from '@services/product/order/order.types';
import { ColumnDef } from '@tanstack/react-table';
import { Table } from '@components/Table/Table/Table';
import { formatMoney } from '@utils/numbers';

export default function DetailsPage() {
    const { isLoading, order, isError, orderProductsColumnHelper } = useDetailsPageVM();

    const orderProductsColumns = [
        orderProductsColumnHelper.accessor('name', {
            header: 'Nom du produit',
            cell: (info) => <>{info.getValue()}</>
        }),
        orderProductsColumnHelper.accessor('quantity', {
            header: 'Quantité',
            size: 50,
            meta: {
                style: {
                    textAlign: 'center'
                }
            },
            cell: (info) => <div style={{ textAlign: 'center' }}>{info.getValue()}</div>
        }),
        orderProductsColumnHelper.accessor('total', {
            header: 'Total HT',
            size: 50,
            meta: {
                style: {
                    textAlign: 'center'
                }
            },
            cell: (info) => <div style={{ textAlign: 'center' }}>{formatMoney(info.getValue())}</div>
        }),
        orderProductsColumnHelper.accessor('totalTax', {
            header: 'Total TVA',
            size: 50,
            meta: {
                style: {
                    textAlign: 'center'
                }
            },
            cell: (info) => <div style={{ textAlign: 'center' }}>{formatMoney(info.getValue())}</div>
        }),
        orderProductsColumnHelper.accessor((row) => row.total + row.totalTax, {
            header: 'Total TTC',
            size: 50,
            meta: {
                style: {
                    textAlign: 'center'
                }
            },
            cell: (info) => <div style={{ textAlign: 'center' }}>{formatMoney(info.getValue())}</div>
        }),
        orderProductsColumnHelper.display({
            id: 'action',
            size: 0,
            header: 'E-billets',
            meta: {
                style: {
                    textAlign: 'center'
                }
            },
            cell: (info) => {
                return (
                    <div style={{ display: 'flex', justifyContent: 'center' }}>
                        {info.row.original.etickets.length > 0 && (
                            <Button
                                variant="icon"
                                color="primary"
                                size="small"
                                icon={<Icon icon={<Pdf />} size={20} color="primary" />}
                                hoverIcon={<Icon icon={<Pdf />} size={20} color="secondary" />}
                                onClick={() => {
                                    window.open(info.row.original.etickets[0].pdfLink, '_blank');
                                }}
                            />
                        )}
                    </div>
                );
            }
        })
    ] as ColumnDef<OrderProduct>[];

    if (isLoading) {
        return <Loader className="full-container-loader" />;
    }

    if (isError) {
        return (
            <Typography intrinsic="p" color="secondary">
                Une erreur est survenue lors de la récupération des informations de la fiche commande
            </Typography>
        );
    }

    if (!order) {
        return <></>;
    }

    return (
        <div className="details-order-container">
            <Card
                className="details-order-container-body"
                headerType="full"
                showBack
                backTo="/products-management/orders"
                headerTitle={'#' + order.woocommerceId + ' - Fiche commande'}
                headerIcon={<Icon icon={<OrdersListLine />} size={20} color="primary" />}
            >
                {order.hasIdentityMismatch && (
                    <div className="identity-mismatch-alert">
                        <Typography intrinsic="p">
                            ⚠️ Les informations de facturation ne correspondent pas au titulaire du compte — commande
                            potentiellement passée par un tiers. L'adhérent affiché reste le titulaire du compte.
                        </Typography>
                    </div>
                )}
                <Grid columns={2}>
                    <Card headerType="very-light" headerTitle={'Informations adhérent'}>
                        {order.member ? (
                            <Grid columns={2}>
                                <LabelValue label="N°COSEM" value={order.member?.cosemNumber} />
                                <LabelValue
                                    label="Type de cotisation"
                                    value={
                                        order.member
                                            ? CONTRIBUTION_TYPE[order.member.administrativeInformation.contributionType]
                                            : undefined
                                    }
                                />
                                <LabelValue label="Nom" value={order.member?.personalInformation.lastname.toUpperCase()} />
                                <LabelValue label="Prénom" value={order.member?.personalInformation.firstname.toUpperCase()} />
                                <LabelValue label="Email" value={order.member?.contactInformation.email} />
                                <LabelValue label="Téléphone" value={order.member?.contactInformation.phone} />
                            </Grid>
                        ) : (
                            <Typography intrinsic="p" color="secondary">
                                Aucun adhérent trouvé pour cette commande
                            </Typography>
                        )}
                    </Card>
                    <Card headerType="very-light" headerTitle={'Informations commande'}>
                        <Grid columns={2}>
                            <LabelValue label="Numéro de commande" value={order.woocommerceId} />
                            <LabelValue label="Date de commande" value={formatDate(order.createdAt)} />
                            <LabelValue label="Statut" value={ORDER_STATUS[order.status]} />
                            <LabelValue label="Mode de paiement" value={PAYMENT_METHOD[order.paymentMethod]} />
                            <LabelValue label="Total" value={formatMoney(order.total + order.totalTax)} />
                        </Grid>
                        {order.invoiceLink && (
                            <Button
                                type="button"
                                className="download-invoice-button"
                                variant="primary"
                                onClick={() => {
                                    window.open(order.invoiceLink, '_blank');
                                }}
                            >
                                Télécharger la facture
                            </Button>
                        )}
                    </Card>
                </Grid>
                <Grid columns={2}>
                    <Card
                        headerType="very-light"
                        headerTitle={'Adresse de facturation utilisée'}
                        className={order.hasIdentityMismatch ? 'billing-card--mismatch' : undefined}
                    >
                        <Grid columns={2}>
                            <LabelValue label="Nom" value={order.billing.lastName} />
                            <LabelValue label="Prénom" value={order.billing.firstName} />
                            <LabelValue label="Société" value={order.billing.company} />
                            <LabelValue label="Adresse" value={order.billing.address1} />
                            <LabelValue label="Complément d'adresse" value={order.billing.address2} />
                            <LabelValue label="Code postal" value={order.billing.postcode} />
                            <LabelValue label="Ville" value={order.billing.city} />
                            <LabelValue label="Pays" value={order.billing.country} />
                            <LabelValue label="Email" value={order.billing.email} />
                            <LabelValue label="Téléphone" value={order.billing.phone} />
                        </Grid>
                    </Card>
                </Grid>

                <Card headerType="very-light" headerTitle={'Liste des produits'}>
                    <Table data={order.orderProducts} columns={orderProductsColumns} />
                </Card>
            </Card>
        </div>
    );
}
